import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateNewPassword, getPasswordError, checkPasswordRequirements } from '../../utils/validation';
import { AccountType } from '../../types/auth';
import Header from '../../components/layout/Header';
import { trackSignup } from '../../utils/analytics';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const VisibilityIcon = MdVisibility as any;
const VisibilityOffIcon = MdVisibilityOff as any;

export default function SignupPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);

  // Account type + legal consent
  const [accountType, setAccountType] = useState<AccountType | ''>('');
  const [parentalConsentGiven, setParentalConsentGiven] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  // Compute password requirements status
  const passwordRequirements = useMemo(() => checkPasswordRequirements(password), [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setIsLoading(true);

    // Validate account type selection
    if (!accountType) {
      setError('Please select who this account is for');
      setIsLoading(false);
      return;
    }

    // Validate parental consent for parent accounts
    if (accountType === 'parent' && !parentalConsentGiven) {
      setError('Please confirm your parental consent to continue');
      setIsLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!termsAccepted) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue');
      setIsLoading(false);
      return;
    }

    // Validate password and confirm password
    const validation = validateNewPassword(password, confirmPassword);
    if (!validation.isValid) {
      setPasswordError(validation.error || 'Invalid password');
      setIsLoading(false);
      return;
    }

    try {
      await signup(fullName, email, password, accountType as AccountType, parentalConsentGiven);
      trackSignup(accountType);
      setSignupComplete(true);
    } catch (error: any) {
      setError(error.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Update password error on change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Show validation errors only if user has started typing in confirm password
    if (confirmPassword) {
      const validation = validateNewPassword(newPassword, confirmPassword);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    } else if (newPassword) {
      // Show password requirements error if only password is filled
      const error = getPasswordError(newPassword);
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    // Validate on change
    if (password && newConfirmPassword) {
      const validation = validateNewPassword(password, newConfirmPassword);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    }
  };

  // If signup is complete, show success message
  if (signupComplete) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background">
        <div className="flex h-full grow flex-col">
          <Header variant="auth" />

          <div className="px-40 flex flex-1 justify-center py-5">
            <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
              <div className="text-center py-8">
                <div className="mb-6">
                  <svg className="w-20 h-20 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                  Check your email
                </h2>
                <p className="text-text-secondary text-base px-4 pb-6">
                  We've sent a verification link to <strong className="text-white">{email}</strong>
                </p>
                <p className="text-text-secondary text-sm px-4 pb-8">
                  Click the "Verify Email Address" button in the email to complete your registration.
                </p>
                <p className="text-text-secondary text-xs px-4 pb-6">
                  Didn't receive the email? Check your spam folder or wait a few minutes.
                </p>
                <Link
                  to="/login"
                  className="text-primary hover:text-primary-hover text-sm font-medium underline"
                >
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div className="flex h-full grow flex-col">
        <Header variant="auth" />

        <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-5">
          <div className="flex flex-col w-full max-w-md md:max-w-lg py-5 flex-1">
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-center pb-3 pt-5">
              Create your account
            </h2>
            {error && (
              <div className="mx-4 mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col items-center w-full">
              {/* Account Type Selection */}
              <div className="flex max-w-[480px] w-full flex-col gap-4 px-4 py-3">
                <label className="flex flex-col w-full">
                  <p className="text-white text-base font-medium leading-normal pb-1">Who is this account for?</p>
                  <p className="text-text-secondary text-xs italic pb-3">If you are younger than 13, your parent or guardian needs to create an account first, then create one for you.</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="accountType"
                        value="parent"
                        checked={accountType === 'parent'}
                        onChange={(e) => {
                          setAccountType(e.target.value as AccountType);
                          setParentalConsentGiven(false);
                        }}
                        className="mt-1 w-4 h-4 text-primary bg-input-bg border-border-light focus:ring-primary"
                      />
                      <span className="text-white text-sm">A parent</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="accountType"
                        value="student"
                        checked={accountType === 'student'}
                        onChange={(e) => setAccountType(e.target.value as AccountType)}
                        className="mt-1 w-4 h-4 text-primary bg-input-bg border-border-light focus:ring-primary"
                      />
                      <span className="text-white text-sm">A learner aged 13 or older</span>
                    </label>
                  </div>
                </label>

                {/* Conditional consent for parent accounts */}
                {accountType === 'parent' && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 mt-2">
                    <p className="text-text-secondary text-sm mb-3">
                      By continuing, you confirm that you are the parent or legal guardian and consent to your child using educAIte.
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parentalConsentGiven}
                        onChange={(e) => setParentalConsentGiven(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-primary bg-input-bg border-border-light focus:ring-primary"
                        required={accountType === 'parent'}
                      />
                      <span className="text-white text-xs">I confirm and consent as parent/guardian</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Full name</p>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] text-base font-normal leading-normal"
                  />
                </label>
              </div>
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Email</p>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] text-base font-normal leading-normal"
                  />
                </label>
              </div>
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Password</p>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Enter your password"
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] pr-12 text-base font-normal leading-normal"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors"
                    >
                      {showPassword ? (
                        <VisibilityIcon className="w-5 h-5" />
                      ) : (
                        <VisibilityOffIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </label>
              </div>
              <div className="flex max-w-[480px] w-full px-4 pb-3">
                <div className="text-xs w-full">
                  <p className={`mb-2 ${password.length === 0 ? 'text-text-secondary' : 'text-white'}`}>Password must:</p>
                  <ul className="space-y-1">
                    <li className={`flex items-center gap-2 ${
                      password.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.minLength
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {password.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.minLength ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Be at least 8 characters long</span>
                    </li>
                    <li className={`flex items-center gap-2 ${
                      password.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.hasNumber
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {password.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.hasNumber ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Contain at least one number</span>
                    </li>
                    <li className={`flex items-center gap-2 ${
                      password.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.hasSpecialChar
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {password.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.hasSpecialChar ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Contain at least one special character</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Confirm Password</p>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="Confirm your password"
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] pr-12 text-base font-normal leading-normal"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? (
                        <VisibilityIcon className="w-5 h-5" />
                      ) : (
                        <VisibilityOffIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </label>
              </div>
              {passwordError && (
                <div className="mx-4 mb-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-yellow-300 text-xs max-w-[480px] w-full">
                  {passwordError}
                </div>
              )}
              {/* Terms of Service + Privacy Policy */}
              <div className="flex max-w-[480px] w-full px-4 py-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-primary bg-input-bg border-border-light focus:ring-primary flex-shrink-0"
                  />
                  <span className="text-text-secondary text-xs leading-relaxed">
                    I agree to the{' '}
                    <a
                      href="https://www.educaite.co/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hover underline"
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href="https://www.educaite.co/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hover underline"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
              <div className="flex px-4 py-3 w-full justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex max-w-[450px] w-full cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-5 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="truncate">
                    {isLoading ? 'Creating account...' : 'Sign up'}
                  </span>
                </button>
              </div>
            </form>
            <div className="text-center mt-6 px-4">
              <p className="text-text-secondary text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-primary hover:text-primary-hover font-medium underline"
                >
                  Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
